import { Button } from '@tempo-io/sui-button';
import { Password } from '@tempo-io/sui-icons';
import { Input } from '@tempo-io/sui-input';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFocusOnOpen } from './hooks/useFocusOnOpen';
import type { PasswordAuthStatus } from './types';

export interface PasswordPromptModalProps {
  /** Auth status determines error display: 'password_invalid' shows error message */
  authStatus: PasswordAuthStatus;
  onSubmit: (password: string) => Promise<void>;
}

export function PasswordPromptModal({ authStatus, onSubmit }: PasswordPromptModalProps) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Submission errors are local state - auth errors derive from props
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const inputRef = useFocusOnOpen<HTMLInputElement>(true);

  // Derive auth error from props (not state) - reacts to prop changes immediately
  const authError =
    authStatus === 'password_invalid'
      ? t(($) => $.views.publishView.passwordPrompt.invalidPassword)
      : null;

  // Combined error for display - auth errors take precedence
  const error = authError ?? submissionError;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting || password.trim() === '') return;

    setIsSubmitting(true);
    setSubmissionError(null);
    try {
      await onSubmit(password.trim());
    } catch (err) {
      setSubmissionError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main
      aria-labelledby="password-prompt-title"
      className="flex min-h-screen w-full items-center justify-center bg-bg-surface-base"
    >
      <div className="w-full max-w-sm px-6">
        {/* Icon */}
        <div className="mb-6 flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-bg-neutral">
            <Password className="h-8 w-8 text-text-neutral-secondary" />
          </div>
        </div>

        {/* Title */}
        <h1
          id="password-prompt-title"
          className="mb-2 text-center text-xl font-semibold text-text-neutral-primary"
        >
          {t(($) => $.views.publishView.passwordPrompt.title)}
        </h1>

        {/* Description */}
        <p className="mb-6 text-center text-sm text-text-neutral-secondary">
          {t(($) => $.views.publishView.passwordPrompt.description)}
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="password-input" className="sr-only">
                {t(($) => $.views.publishView.passwordPrompt.label)}
              </label>
              <Input
                ref={inputRef}
                id="password-input"
                type="password"
                autoComplete="new-password"
                placeholder={t(($) => $.views.publishView.passwordPrompt.label)}
                aria-invalid={!!error}
                aria-describedby={error ? 'password-error' : undefined}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  // Only clear submission errors on typing - auth errors controlled by parent
                  if (submissionError) setSubmissionError(null);
                }}
                disabled={isSubmitting}
                className="focus:ring-0 focus:border-border-neutral focus-visible:ring-0 focus-visible:border-border-neutral"
              />
            </div>

            {error && (
              <div id="password-error" className="text-sm text-text-error" aria-live="polite">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={isSubmitting || password.trim() === ''}
              variant="default"
              className="w-full"
            >
              {t(($) => $.views.publishView.passwordPrompt.submit)}
            </Button>
          </div>
        </form>
      </div>
    </main>
  );
}
